import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { ThemeSettings } from '../../theme';
import { LoadingIndicator, PseudoLink2 } from '../../toolympus/components/primitives';
import { Typography } from '@mui/material';
import { Contact } from './types';
import { useContactsTags } from './useContactsTagsConfiguration';
import { TagsCondensedDisplay } from '../Projects/ProjectTagsPicker';
import { EditRecordAttributeData } from '../Projects/useEditProjectAttribute';
import { ProjectField } from '../Projects/useProjectsSettings';
import { ContactsListData } from './useContactsList';
import { useHistory } from 'react-router-dom';

// Reusing styling logic or creating new components for Contacts
const FieldEditor = styled.div`
  position: relative;
  & textarea {
    width: 100%;
    border: none;
    border-bottom: 1.5px solid ${ThemeSettings.colors.table.editor_border};
    background: transparent;
    outline: none;
    font-size: inherit;
    font-family: inherit;
    resize: none;
  }

  & .spinner {
    position: absolute;
    top: 50%;
    right: 100%;
    transform: translateY(-50%);
  }
`;


const ContactRow = styled.tr<{ isShowingTasks?: boolean }>`
  position: relative;
  & td {
    padding: 12px 6px;
    border-bottom: 1.5px solid ${ThemeSettings.colors.table.border};

    &:first-child {
      /* padding-left: 0; */
      cursor: pointer;
    }
    &:last-child {
      padding-right: 0;
    }
  }

  & td.current-tasks-column {
    width: ${props => props.isShowingTasks ? "300px" : "0"};
    text-align: ${props => props.isShowingTasks ? "inherit" : "right"};

    & .task-count-blip {
      font-size: ${ThemeSettings.typography.fontSizes.smaller};
      opacity: 0.75;
    }
  }
`;

const ContactsTable = styled.table`
  width: 100%;

  & thead {
    font-size: ${ThemeSettings.typography.fontSizes.smaller};
    color: ${ThemeSettings.colors.textSecondary};

    & td, & th {
      ${PseudoLink2} {
        margin-left: 0.25rem;
        font-size: ${ThemeSettings.typography.fontSizes.tiny};
      }
    }

    & td.current-tasks-column {
      white-space: nowrap;
    }
  }

  & tr {
    & td:first-child {
      white-space: nowrap;
    }

    & td {
      white-space: pre-line;
    }
  }
`;


export const ContactsTagsCondensedDisplay = (props: { tags: string[] | undefined }) => {
  const { tags } = useContactsTags();

  return (
    <TagsCondensedDisplay options={tags} tags={props.tags} />
  )
}


interface Props {
  fields: ProjectField[];
  
  data: ContactsListData;
  editAttributes: EditRecordAttributeData<Contact>;

  startEdit: (c: Contact) => void;
  
}

export const ContactsList = (props: Props) => {
  const {
    fields,
    data,
    editAttributes,
    startEdit,
  } = props;

  const history = useHistory();

  useEffect(() => {
    const query = new URLSearchParams(history.location.search);
    const contactName = query.get("show");
    if(contactName && contactName.trim()) {
      data.filter.setFilter(contactName);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ContactsTable>
      <thead>
        <tr>
          <td></td>
          {fields.map(f => (
            <td key={f.key}>
              {f.title || "..."}
            </td>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.data.map(c => (
          <ContactRow
            key={c._id}>
            <td onClick={e => { startEdit(c)}}>
              <Typography key={c._id}>
                {c.name}
              </Typography>
              {/* Reuse tags display or create new one */}
              <ContactsTagsCondensedDisplay tags={c.tags} />
            </td>

            {fields.map(f => (
              <td key={f.key} onClick={e => { e.stopPropagation(); e.preventDefault(); editAttributes.startEditing(c._id, f.key as string, (c as any)[f.key] || "") }}>
                {editAttributes.editing?.recordId === c._id && editAttributes.editing?.field === f.key
                  ? (
                    <FieldEditor>
                      <textarea
                        autoFocus
                        value={editAttributes.editing.value || ""}
                        onChange={e => editAttributes.update(e.target.value)}
                        onBlur={() => editAttributes.complete()}
                        />
                      {!!editAttributes.editing.isSaving && <LoadingIndicator className="spinner" sizeVariant="s" />}
                    </FieldEditor>
                  )
                  : (c as any)[f.key] || ""}
              </td>
            ))}

            
          </ContactRow>
        ))}

        {data.limitTrigger.pagingAnchor}
      </tbody>
    </ContactsTable>
  );
}
